import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check, Settings2 } from "lucide-react";

const KEY = "estrofa_cookie_consent_v1";
const Ctx = createContext(null);
export const useCookieConsent = () => useContext(Ctx);

function read() {
  try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
}

export function CookieProvider({ children }) {
  const [consent, setConsent] = useState(() => read());
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => { if (!read()) setShowBanner(true); }, []);

  const save = useCallback((c) => {
    const full = { necessary: true, analytics: !!c.analytics, thirdparty: !!c.thirdparty, ts: new Date().toISOString(), version: 1 };
    localStorage.setItem(KEY, JSON.stringify(full));
    setConsent(full); setShowBanner(false); setShowSettings(false);
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: full }));
  }, []);

  const acceptAll = () => save({ analytics: true, thirdparty: true });
  const rejectAll = () => save({ analytics: false, thirdparty: false });
  const openSettings = useCallback(() => setShowSettings(true), []);

  return (
    <Ctx.Provider value={{ consent, acceptAll, rejectAll, save, openSettings }}>
      {children}
      <CookieBanner show={showBanner} onAccept={acceptAll} onReject={rejectAll} onConfig={() => setShowSettings(true)} />
      <CookieSettings open={showSettings} consent={consent} onClose={() => setShowSettings(false)} onSave={save} />
    </Ctx.Provider>
  );
}

function CookieBanner({ show, onAccept, onReject, onConfig }) {
  if (!show) return null;
  return (
    <div className="anim-slide-up fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[9999]" data-testid="cookie-banner">
          <div className="bg-[#1A1A1A] text-[#FAF9F6] rounded-3xl shadow-2xl p-6 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Cookie size={20} className="text-[#B4D844]" />
              <h3 className="display text-xl">Cookies en La Estrofa</h3>
            </div>
            <p className="text-sm text-white/70 leading-relaxed mb-5">
              Usamos cookies necesarias para el funcionamiento del sitio y, con tu permiso, cookies de terceros (Spotify, YouTube, Instagram, X) y analíticas de <strong className="text-white">Google Analytics (GA4)</strong>. Puedes aceptarlas, rechazarlas o configurarlas. Más info en la{" "}
              <Link to="/politica-de-cookies" className="text-[#B4D844] hover:underline">Política de Cookies</Link>.
            </p>
            <div className="flex flex-wrap gap-2">
              <button onClick={onAccept} className="btn-lime !py-2.5 !px-5 text-sm flex-1 justify-center" data-testid="cookie-accept-all">Aceptar todo</button>
              <button onClick={onReject} className="rounded-full border border-white/25 hover:bg-white/10 text-white font-semibold px-5 py-2.5 text-sm transition-colors flex-1" data-testid="cookie-reject-all">Rechazar</button>
              <button onClick={onConfig} className="rounded-full text-white/70 hover:text-white px-3 py-2.5 text-sm inline-flex items-center gap-1.5 w-full justify-center mt-1" data-testid="cookie-configure">
                <Settings2 size={15} /> Configurar
              </button>
            </div>
          </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? "bg-[#2A00D9]" : "bg-black/20"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function CookieSettings({ open, consent, onClose, onSave }) {
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [thirdparty, setThirdparty] = useState(consent?.thirdparty ?? false);
  useEffect(() => { if (open) { setAnalytics(consent?.analytics ?? false); setThirdparty(consent?.thirdparty ?? false); } }, [open, consent]);

  if (!open) return null;
  return (
    <div className="anim-fade-in fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="cookie-settings">
          <div onClick={(e) => e.stopPropagation()}
            className="anim-pop-in bg-[#FAF9F6] rounded-3xl shadow-2xl w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="display text-2xl flex items-center gap-2"><Cookie size={20} className="text-[#2A00D9]" /> Preferencias de cookies</h3>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5" data-testid="cookie-settings-close"><X size={18} /></button>
            </div>
            <p className="text-sm text-[#4A4A4A] mb-6">Elige qué cookies permites. Puedes cambiarlo cuando quieras desde "Configurar cookies" en el pie de página.</p>

            <div className="space-y-3">
              <div className="rounded-2xl border border-black/10 p-4 flex items-start justify-between gap-4">
                <div><p className="font-semibold text-sm">Necesarias</p><p className="text-xs text-[#4A4A4A]">Imprescindibles para el funcionamiento del sitio. Siempre activas.</p></div>
                <Toggle checked disabled onChange={() => {}} />
              </div>
              <div className="rounded-2xl border border-black/10 p-4 flex items-start justify-between gap-4">
                <div><p className="font-semibold text-sm">Terceros / Contenido incrustado</p><p className="text-xs text-[#4A4A4A]">Spotify, YouTube, Instagram y X. Necesarias para ver reproductores y publicaciones incrustadas.</p></div>
                <Toggle checked={thirdparty} onChange={setThirdparty} />
              </div>
              <div className="rounded-2xl border border-black/10 p-4 flex items-start justify-between gap-4">
                <div><p className="font-semibold text-sm">Analíticas</p><p className="text-xs text-[#4A4A4A]">Usamos <strong>Google Analytics (GA4)</strong> para entender de forma anónima qué contenidos interesan más (páginas vistas, sesiones, origen del tráfico). Solo se activan con tu permiso.</p></div>
                <Toggle checked={analytics} onChange={setAnalytics} />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              <button onClick={() => onSave({ analytics, thirdparty })} className="btn-cobalt flex-1 justify-center" data-testid="cookie-settings-save">
                <Check size={16} /> Guardar preferencias
              </button>
              <button onClick={() => onSave({ analytics: true, thirdparty: true })} className="rounded-full border border-black/15 hover:bg-black/5 font-semibold px-5 py-3 text-sm transition-colors" data-testid="cookie-settings-accept-all">Aceptar todo</button>
            </div>
          </div>
    </div>
  );
}

export function ConsentGate({ children, label = "este contenido" }) {
  const ctx = useCookieConsent();
  if (ctx?.consent?.thirdparty) return children;
  return (
    <div className="rounded-2xl border border-black/10 bg-[#F3F2EE] p-6 md:p-8 text-center flex flex-col items-center gap-3" data-testid="consent-gate">
      <Cookie size={28} className="text-[#2A00D9]" />
      <p className="text-sm text-[#4A4A4A] max-w-sm">Para ver {label} necesitamos tu permiso para <strong>cookies de terceros</strong>.</p>
      <div className="flex flex-wrap gap-2 justify-center">
        <button onClick={() => ctx?.save({ analytics: ctx?.consent?.analytics, thirdparty: true })} className="btn-cobalt !py-2 !px-4 text-sm" data-testid="consent-gate-accept">Activar contenido</button>
        <button onClick={() => ctx?.openSettings()} className="text-sm text-[#2A00D9] underline px-3 py-2">Configurar</button>
      </div>
    </div>
  );
}
